// Shared post-sign-in sequence for the console.
//
// After Amplify has a session (Login.vue sign in, or AcceptInvite.vue right after the
// invite sets a password), this loads the profile and permissions into localStorage,
// picks the default group, and routes to the landing page. Behavior and localStorage
// keys are the same ones Login.vue has always written; both callers share this so the
// two entry points cannot drift.
import { Auth } from 'aws-amplify'
import axios from 'axios'
import swal from 'sweetalert2'
import { API_LOCATION } from '@/api-config'
import { fetchGroupSummaries, fetchFullGroup, pickLoginGroupId, writeActiveGroup, LAST_GROUP_KEY } from '@/util/activeGroup'
import { clearGroupDataCache } from '@/util/groupDataCache'

async function getAuthToken() {
  const session = await Auth.currentSession()
  return session.idToken.jwtToken
}

const LANDING_OPTIONS = {
  bookings:   { path: '/bookings',                              permission: 'bookings_view' },
  dashboard:  { path: '/dashboard',                             permission: 'dashboards_view' },
  games:      { path: '/games/tracker',                         permission: 'game_tracker_submit' },
  mail:       { path: '/mail/custom/landing',                   permission: 'automated_emails_update' },
  purchases:  { path: '/purchases/transactions?count=10&page=1', permission: 'transactions_view' },
  projects:   { path: '/projects?count=10&page=1',              permission: 'project_view' },
  customers:  { path: '/customers?count=10&page=1',             permission: 'customer_view' },
  waivers:    { path: '/waivers/history?count=10&page=1',       permission: 'waivers_view' },
  settings:   { path: '/settings/profile',                      permission: null },
}

// Configured landing page: custom role override first, then the group default.
function resolveLandingPage() {
  let group, profile, permissions
  try {
    group = JSON.parse(localStorage.getItem('group'))
    profile = JSON.parse(localStorage.getItem('profile'))
    permissions = JSON.parse(localStorage.getItem('permissions')) || []
  } catch (e) {
    console.error('Failed to parse localStorage for landing page resolution', e)
    return null
  }

  const roleKey = profile?.role_landing_page
  if (roleKey && LANDING_OPTIONS[roleKey]) {
    const option = LANDING_OPTIONS[roleKey]
    if (!option.permission || permissions.includes(option.permission)) return option.path
  }

  const groupKey = group?.default_landing_page
  if (groupKey && LANDING_OPTIONS[groupKey]) {
    const option = LANDING_OPTIONS[groupKey]
    if (!option.permission || permissions.includes(option.permission)) return option.path
  }

  return null
}

// Home destination for a signed-in user: configured landing page, else bookings when
// permitted, else the profile page. Used after sign in and by the root route, so opening
// the bare domain with an existing session lands where a fresh login would.
export function resolveHomePath() {
  const configuredPage = resolveLandingPage()
  if (configuredPage) return configuredPage
  let permissions = []
  try {
    permissions = JSON.parse(localStorage.getItem('permissions')) || []
  } catch (e) {
    console.error('Failed to parse permissions for home path resolution', e)
  }
  return Array.isArray(permissions) && permissions.includes('bookings_view') ? '/bookings' : '/settings/profile'
}

async function loadProfile() {
  const token = await getAuthToken()
  const config = { headers: { Authorization: token } }
  const response = await axios.get(API_LOCATION + 'users/me', config)
  localStorage.setItem('companyId', response.data.companyId)
  localStorage.setItem('profile', JSON.stringify(response.data.profile))
  localStorage.setItem('company', JSON.stringify(response.data.company))
  sessionStorage.removeItem('companyInfoReminderShown')
  localStorage.setItem('permissions', JSON.stringify(response.data.permissions))
  localStorage.setItem('permissionLevels', JSON.stringify(response.data.permissionLevels || {}))
  localStorage.setItem('mode', response.data.profile.site_mode ?? 1)
  return response.data
}

// Lands on this device's last location when still allowed, else the server default,
// else the first by position. Only the chosen location's full row is fetched.
async function loadGroups() {
  const groups = await fetchGroupSummaries()
  const lastId = Number(localStorage.getItem(LAST_GROUP_KEY)) || null
  const id = pickLoginGroupId(groups, lastId)
  if (id) {
    const full = await fetchFullGroup(id)
    if (full) writeActiveGroup(full)
  }
  return groups
}

// options.landing: an explicit destination that wins over every default (used by the
// invite accept page to send a new tenant admin to the setup wizard).
export async function completePostSignIn(router, options = {}) {
  // Every sign in passes here, including ones not preceded by signOut() (AcceptInvite,
  // BillingPage). A cached users/me from the previous user would overwrite the
  // permissions loadProfile writes (OTC-3151).
  clearGroupDataCache()
  await loadProfile()

  try {
    await loadGroups()
  } catch (err) {
    if (err.response && err.response.status == 401) {
      await swal({
        title: 'Error',
        html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
        type: 'error',
      })
      // Explicit, not '/': the root route resolves a landing page from the group
      // that just failed to load.
      router.push('/settings/profile')
      return
    }
    throw err
  }

  const lastVisitedRoute = localStorage.getItem('lastVisitedRoute')
  localStorage.removeItem('lastVisitedRoute')

  if (options.landing) {
    router.push(options.landing)
    return
  }
  const configuredPage = resolveLandingPage()
  if (configuredPage) {
    router.push(configuredPage)
  } else if (lastVisitedRoute && lastVisitedRoute !== '/login' && lastVisitedRoute !== '/register') {
    router.push(lastVisitedRoute)
  } else {
    router.push(resolveHomePath())
  }
}
